import { useState } from 'react'
import { NAV_ATELIER, NAV_BARRE, NAV_FEUILLE, NAV_EDITORIAL, type NavEntry } from './navigation'

interface Props {
  active: string
  /**
   * L'espace où l'on se trouve — RG-D25bis, [DECISIONS §115].
   *
   * Dans l'atelier, la barre liste les quatre postes de travail et le bouton
   * central ouvre le retour au front-office : un éditeur sur téléphone n'a pas
   * plus besoin du Fil dans sa barre qu'un candidat n'a besoin de l'Établi.
   */
  espace?: 'candidat' | 'editorial'
  /** RG-D25 — le back-office n'apparaît dans la feuille que s'il est ouvert. */
  editorial?: boolean
  /**
   * Le sigle du concours actuellement principal — §78.
   *
   * `null` tant qu'aucun n'est désigné : la ligne dit alors « Principal » tout
   * court, plutôt qu'un tiret qui se lirait comme une valeur.
   */
  concoursPrincipal?: string | null
  onNavigate: (id: string) => void
}

/**
 * Barre d'onglets du bas — RG-D25, [DECISIONS §47].
 *
 * **Quatre destinations et un bouton central.** Fil, Formations, Archives et
 * Mon Cercle sont les quatre lieux où l'on travaille : on y va souvent et on y
 * reste. Notifications, Classement, Compte, Thèmes et le back-office sont des
 * détours — on les ouvre, on regarde, on revient — et ils vivent derrière le
 * bouton du milieu.
 *
 * **Pourquoi ne pas tout mettre au même rang.** Neuf entrées sur un écran de
 * 360 px font des cibles de 40 px, sous le seuil tactile de 44, et diluent les
 * quatre qui comptent. Le tri par fréquence d'usage est ce qui rend une barre
 * lisible ; l'exhaustivité la rend inutilisable.
 *
 * **Pourquoi le bouton au centre.** C'est la seule place qu'aucun onglet ne
 * peut revendiquer sans rompre la symétrie, et celle que le pouce atteint le
 * plus facilement d'une main — quelle que soit la main. Il déborde vers le
 * haut : posé à plat, un rond de la couleur d'accent au milieu d'icônes en
 * trait se lit comme un cinquième onglet, ce qu'il n'est pas.
 *
 * **Il ne prend jamais l'état actif.** Ce n'est pas une destination : l'onglet
 * souligné reste celui de l'écran où l'on se trouve, feuille ouverte ou non.
 *
 * Disparaît dès 688 px : au-delà, c'est le rail latéral qui prend le relais.
 */
export function BottomTabs({
  active,
  editorial = false,
  concoursPrincipal = null,
  espace = 'candidat',
  onNavigate,
}: Props) {
  const [feuilleOuverte, setFeuilleOuverte] = useState(false)

  const atelier = espace === 'editorial'

  // **Quatre postes dans la barre, le cinquième dans la feuille** — §115. La
  // barre n'a que quatre places de part et d'autre du bouton central : les
  // forcer à cinq casserait la symétrie et ferait des cibles sous le seuil
  // tactile, ce que RG-D25 refuse depuis le premier jour.
  const barre = atelier ? NAV_ATELIER.filter((e) => e.primary).slice(0, 4) : NAV_BARRE

  const gauche = barre.slice(0, 2)
  const droite = barre.slice(2)

  const secondaires = atelier
    ? NAV_ATELIER.filter((e) => !barre.includes(e))
    : editorial
      ? [...NAV_FEUILLE, NAV_EDITORIAL]
      : NAV_FEUILLE

  return (
    <>
      {feuilleOuverte && (
        <FeuilleDeNavigation
          entrees={secondaires}
          active={active}
          concoursPrincipal={concoursPrincipal}
          onChoisir={(id) => {
            setFeuilleOuverte(false)
            onNavigate(id)
          }}
          onFermer={() => {
            setFeuilleOuverte(false)
          }}
        />
      )}

      <nav
        aria-label="Navigation du bas"
        // **Pas de `border-t` : le trait est dessiné**, parce qu'il doit
        // contourner le bouton central. Une bordure CSS ne sait pas s'écarter
        // d'un rond posé dessus ; elle le coupait en deux, et le bouton avait
        // l'air collé sur la barre plutôt que serti dedans.
        className="fixed inset-x-0 bottom-0 z-30 flex min-[688px]:hidden"
      >
        <TraitEncoche />

        {gauche.map((entry) => (
          <Onglet
            key={entry.id}
            entry={entry}
            courant={entry.id === active}
            onNavigate={onNavigate}
          />
        ))}

        {/* La cellule centrale garde sa place dans la répartition ; le bouton,
            lui, déborde vers le haut, dans le berceau que `TraitEncoche`
            découpe pour lui. Sans cellule, les quatre onglets se répartiraient
            toute la largeur et le bouton les recouvrirait.
            `w-24` : la largeur exacte du morceau échancré (96 px), pour que le
            bouton tombe au centre du berceau et non à côté. */}
        <div className="relative flex h-[53px] w-24 shrink-0 items-start justify-center">
          <button
            type="button"
            aria-expanded={feuilleOuverte}
            aria-label={feuilleOuverte ? 'Fermer le menu' : 'Ouvrir le menu'}
            onClick={() => {
              setFeuilleOuverte((avant) => !avant)
            }}
            // 56 px de diamètre dans une découpe de 66 : 5 px de jeu tout
            // autour, et le même partout — c'est ce que le berceau circulaire
            // garantit, là où le dôme se resserrait aux épaules et disparaissait
            // derrière le bouton.
            // `-mt-7` : le CENTRE du bouton tombe exactement sur le trait. Sa
            // moitié haute flotte au-dessus de la barre, sa moitié basse est
            // cernée par la découpe.
            className="-mt-7 flex size-14 items-center justify-center rounded-full shadow-lg transition-transform"
            style={{
              background: 'var(--accent-fort)',
              color: 'var(--sur-accent)',
              // La croix est le même trait tourné : une seconde icône ferait
              // sauter le glyphe au lieu de le faire pivoter.
              transform: feuilleOuverte ? 'rotate(45deg)' : undefined,
            }}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" className="size-6" fill="currentColor">
              <path d="M11 4h2v7h7v2h-7v7h-2v-7H4v-2h7V4z" />
            </svg>
          </button>
        </div>

        {droite.map((entry) => (
          <Onglet
            key={entry.id}
            entry={entry}
            courant={entry.id === active}
            onNavigate={onNavigate}
          />
        ))}
      </nav>
    </>
  )
}

/**
 * Le fond de la barre et son trait supérieur, ÉCHANCRÉ autour du bouton.
 *
 * **Une bordure CSS ne sait pas s'écarter.** `border-t` traçait une droite que
 * le bouton coupait en deux : le rond avait l'air posé SUR la barre, pas serti
 * dedans, et le trait réapparaissait de part et d'autre comme une rayure. Le
 * trait est donc dessiné — trois morceaux qui se rejoignent exactement.
 *
 * `pointer-events-none` : ce n'est qu'un décor, et il couvre toute la largeur.
 * Sans cela il avalerait les tapes sur les onglets.
 */
function TraitEncoche() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end"
      style={{ color: 'var(--bordure)' }}
    >
      <Cote />

      <svg
        width={96}
        height={82}
        viewBox="0 0 96 82"
        className="block shrink-0"
        // Le fond de la barre est OPAQUE : la carte qui défile dessous
        // apparaîtrait sinon dans l'échancrure.
        fill="var(--fond)"
      >
        {/* Le fond, berceau compris. */}
        <path d={`${BERCEAU} V82 H0 Z`} />

        {/* Le trait, sur le seul contour supérieur.
            `strokeLinecap="round"` : les deux extrémités rejoignent le trait
            plat des côtés ; un bout carré y laissait une marche d'un demi-pixel
            que l'œil lit comme une cassure. */}
        <path
          d={BERCEAU}
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      <Cote />
    </div>
  )
}

/**
 * Le contour de la barre autour du bouton — un BERCEAU, sous lui.
 *
 * **Le contournement se fait par le BAS.** Le tracé précédent était un dôme :
 * il passait au-dessus de la tête du bouton, à trois unités de son sommet. Deux
 * défauts, et le second est le vrai : le jeu n'était de trois unités qu'AU
 * SOMMET — sur les flancs, la cubique redescendait plus vite que le cercle du
 * bouton et le trait passait DERRIÈRE lui. D'où « il est même caché en certains
 * endroits par le bouton ».
 *
 * **Un cercle concentrique règle le jeu une fois pour toutes.** La découpe est
 * l'arc du cercle de rayon 33 centré au même point que le bouton, qui en fait
 * 28 : l'écart vaut 5 EN TOUT POINT, par construction — c'est une propriété de
 * deux cercles concentriques, pas un réglage à retoucher.
 *
 * **Et les raccords sont adoucis** (§66). L'arc rejoindrait la droite à
 * tangente verticale : un angle droit franc de chaque côté. Deux congés de
 * rayon 10, tangents à la droite ET au cercle de découpe, remplacent ces angles
 * — la tangente est horizontale en entrant, et continue jusqu'à la découpe.
 *
 * Repères, dans un système de 96 × 82 : le haut de la barre est à y = 29, le
 * bouton est centré en (48, 29) — son centre tombe SUR le trait, sa moitié
 * haute flotte au-dessus.
 *
 * - congé gauche : de (6.2, 29) à (15.9, 36.7), rayon 10, sens horaire ;
 * - berceau : de (15.9, 36.7) à (80.1, 36.7), rayon 33, par le bas ;
 * - congé droit : le miroir du premier.
 */
const BERCEAU =
  'M0 29 L6.2 29 A10 10 0 0 1 15.9 36.7 A33 33 0 0 0 80.1 36.7 A10 10 0 0 1 89.8 29 L96 29'

/**
 * Un côté plat de la barre : le fond, et le trait du haut.
 *
 * **Le trait est dessiné, pas une bordure CSS.** `border-t` produisait une
 * ligne d'un pixel physique quand le trait du dôme, lui, est mis à l'échelle du
 * `viewBox` : sur un écran à densité non entière, les deux ne tombaient pas sur
 * la même rangée de pixels et le trait paraissait continuer derrière le bouton,
 * décalé d'un demi-pixel. Un `<svg>` de part et d'autre, tracé dans le même
 * système, les aligne exactement.
 */
function Cote() {
  return (
    // **Même hauteur et même repère que le dôme** — 82 unités, trait à y = 29.
    // C'est ce qui garantit que les trois morceaux tracent la MÊME ligne : des
    // repères différents la faisaient tomber sur deux rangées de pixels
    // voisines, et le trait paraissait se dédoubler derrière le bouton.
    // Au-dessus de y = 29, le côté est transparent : seul le dôme occupe cette
    // bande.
    <svg
      className="block h-[82px] min-w-0 flex-1"
      viewBox="0 0 10 82"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <rect x="0" y="29" width="10" height="53" fill="var(--fond)" />
      {/* `vector-effect` : le trait garde son épaisseur malgré l'étirement
          horizontal du `preserveAspectRatio="none"`. */}
      <path
        d="M0 29 H10"
        stroke="currentColor"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

function Onglet({
  entry,
  courant,
  onNavigate,
}: {
  entry: NavEntry
  courant: boolean
  onNavigate: (id: string) => void
}) {
  return (
    <button
      type="button"
      aria-current={courant ? 'page' : undefined}
      aria-label={entry.label}
      onClick={() => {
        onNavigate(entry.id)
      }}
      // RG-11 — 53 px de haut : la zone tactile de Twitter, au-dessus du
      // minimum de 44 px.
      // `relative` : le fond dessiné est en `absolute` et passerait sinon
      // par-dessus les icônes, qui disparaîtraient.
      className="relative flex h-[53px] flex-1 items-center justify-center"
      style={{ color: courant ? 'var(--texte-1)' : 'var(--texte-2)' }}
    >
      {entry.icon(courant)}
    </button>
  )
}

/**
 * Ce que le bouton central ouvre.
 *
 * **Une liste, pas un éventail d'icônes.** Les entrées sont peu nombreuses et
 * portent des mots : une liste se lit, se navigue au clavier, et s'annonce
 * correctement à un lecteur d'écran. Un menu radial serait plus joli et moins
 * utilisable.
 *
 * **Elle monte du bas** parce que c'est de là que vient le geste : une feuille
 * qui apparaîtrait au centre de l'écran romprait le lien avec le bouton qu'on
 * vient de taper.
 */
function FeuilleDeNavigation({
  entrees,
  active,
  concoursPrincipal,
  onChoisir,
  onFermer,
}: {
  entrees: NavEntry[]
  active: string
  concoursPrincipal: string | null
  onChoisir: (id: string) => void
  onFermer: () => void
}) {
  return (
    <div
      className="fixed inset-0 z-30 flex items-end justify-center min-[688px]:hidden"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={onFermer}
      role="presentation"
    >
      <div
        role="dialog"
        aria-label="Plus d’options"
        // `pb-[85px]` : la feuille s'arrête AU-DESSUS de la barre et du bouton,
        // qui restent visibles — le bouton porte alors une croix, et c'est lui
        // qui referme.
        //
        // **`max-h-full` et `overflow-y-auto` réparent une feuille qui se
        // coupait.** Le conteneur aligne la feuille en bas (`items-end`) : dès
        // que son contenu dépassait la hauteur de l'écran, le débordement
        // partait vers le HAUT, hors du cadre, et rien ne défilait. Les entrées
        // perdues étaient les premières de la liste — « Principal » et
        // « Thèmes », c'est-à-dire les deux qui comptent le plus.
        //
        // Le calcul : sept entrées de 52 px, six intervalles de 4, 12 px de
        // marge haute et 85 de marge basse font 541 px. Un iPhone SE sous
        // Safari offre environ 460 px de hauteur utile, et un téléphone tenu à
        // l'horizontale bien moins. Le back-office ouvert ajoute une huitième
        // entrée. Le débordement n'était donc pas un cas limite.
        //
        // `overscroll-contain` : sans lui, arriver au bout de la feuille fait
        // défiler le Fil derrière elle.
        className="flex w-full max-w-lg flex-col gap-1 overflow-y-auto overscroll-contain rounded-t-3xl p-3 pb-[85px]"
        style={{
          background: 'var(--fond)',
          // **`dvh` et non `max-h-full`.** Le pourcentage se résout contre le
          // parent `fixed inset-0`, dont la hauteur suit le viewport de MISE EN
          // PAGE : sur mobile, celui-ci reste à sa taille pleine pendant que la
          // barre d'adresse du navigateur mange le bas de l'écran, et la feuille
          // se croyait alors plus de place qu'elle n'en avait. `dvh` suit le
          // viewport RÉELLEMENT visible, qui est celui où le doigt tape.
          maxHeight: '100dvh',
        }}
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        {entrees.map((entry) => {
          const courant = entry.id === active

          return (
            <button
              key={entry.id}
              type="button"
              aria-current={courant ? 'page' : undefined}
              onClick={() => {
                onChoisir(entry.id)
              }}
              // `shrink-0` : dans une colonne qui défile, une entrée reste à sa
              // taille. Sans lui, le navigateur préfère tasser les huit lignes
              // plutôt que faire défiler, et les icônes s'écrasent.
              className="flex min-h-[52px] shrink-0 items-center gap-4 rounded-2xl px-3 text-left text-[17px] font-bold"
              style={{
                color: courant ? 'var(--texte-1)' : 'var(--texte-2)',
                background: courant ? 'var(--surface-2)' : undefined,
              }}
            >
              {entry.icon(courant)}

              {/* **« Principal » porte SA valeur** — §78. « Concours préparé »
                  disait où l'on va sans dire où l'on est, et c'est cette
                  seconde information qu'on vient chercher : on ouvre cette
                  ligne pour vérifier, bien plus souvent que pour changer.
                  La valeur est à la couleur d'accent — c'est la seule donnée
                  de la feuille, tout le reste n'est que destinations. */}
              {entry.id === 'concours' && concoursPrincipal !== null ? (
                <span className="flex min-w-0 items-baseline gap-2">
                  <span>{entry.label}</span>
                  <span className="truncate" style={{ color: 'var(--accent-fort)' }}>
                    {concoursPrincipal}
                  </span>
                </span>
              ) : (
                entry.label
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
